import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import { useUserContext } from '@/lib/auth/useUserContext';
import { formatShortDate } from '@/lib/format';

interface DocumentSummary {
  id: string;
  title: string;
  category: string;
  audience: string;
  ownerEntraOid: string | null;
  reviewCadenceDays: number;
  lastReviewedAt: string | null;
  createdAt: string;
}

interface DocumentDetail extends DocumentSummary {
  version: number | null;
  markdown: string;
  updatedAt: string | null;
}

/**
 * Lightweight markdown renderer. The v1 document library uses Markdown as the
 * source of truth (architecture §4.7.2); we render a small subset locally so
 * we don't pull a 50KB markdown lib into the kiosk bundle for what amounts to
 * SOPs and runbooks. Headings, bold/italic, lists, links, and code blocks.
 *
 * If we need full CommonMark later we can swap to react-markdown — the API
 * shape (currentVersion.markdown) doesn't change.
 */
function renderMarkdown(md: string): string {
  let html = md.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // fenced code blocks (no language)
  html = html.replace(/```([\s\S]*?)```/g, (_m, code: string) => {
    return `<pre class="bg-muted p-2 rounded-md overflow-x-auto text-xs"><code>${code}</code></pre>`;
  });
  // headings
  html = html
    .replace(/^### (.*)$/gm, '<h3 class="text-base font-semibold mt-3">$1</h3>')
    .replace(/^## (.*)$/gm, '<h2 class="text-lg font-semibold mt-4">$1</h2>')
    .replace(/^# (.*)$/gm, '<h1 class="text-xl font-semibold mt-4">$1</h1>');
  // bold/italic
  html = html
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
  // inline code
  html = html.replace(/`([^`]+)`/g, '<code class="bg-muted px-1 rounded">$1</code>');
  // links
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a class="underline underline-offset-4" target="_blank" rel="noopener noreferrer" href="$2">$1</a>',
  );
  // unordered list (simple, one level)
  html = html.replace(/^(?:- (.*)(?:\n|$))+?/gm, (match) => {
    const items = match
      .trim()
      .split(/\n/)
      .map((line) => `<li>${line.replace(/^- /, '')}</li>`)
      .join('');
    return `<ul class="list-disc pl-5 my-2">${items}</ul>`;
  });
  // paragraphs (anything not consumed)
  html = html
    .split(/\n{2,}/)
    .map((block) =>
      block.startsWith('<') ? block : `<p class="my-2">${block.replace(/\n/g, '<br/>')}</p>`,
    )
    .join('\n');
  return html;
}

export function Documents() {
  const { tier } = useUserContext();
  const [rows, setRows] = useState<DocumentSummary[]>([]);
  const [category, setCategory] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    const p = new URLSearchParams();
    if (category !== 'all') p.set('category', category);
    if (search) {
      // Use full-text body search when the term is 3+ chars; LIKE on title
      // for shorter queries (cheaper, still useful).
      if (search.length >= 3) p.set('fts', search);
      else p.set('search', search);
    }
    apiFetch<{ documents: DocumentSummary[] }>(`/documents?${p.toString()}`)
      .then((r) => setRows(r.documents))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [category, search]);

  useEffect(() => {
    reload();
  }, [reload]);

  const categories = Array.from(new Set(rows.map((d) => d.category))).sort();

  return (
    <div className="min-h-screen p-6 max-w-4xl mx-auto">
      <header className="flex items-center justify-between border-b border-border pb-4 mb-4">
        <h1 className="text-2xl font-semibold">Documents</h1>
        {tier === 'manager' ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
          >
            New document
          </button>
        ) : null}
      </header>

      <div className="flex items-center gap-3 mb-4">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="border border-border rounded-md px-2 py-1 text-sm"
        >
          <option value="all">all categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="search title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') reload();
          }}
          className="flex-1 border border-border rounded-md px-3 py-1 text-sm"
        />
      </div>

      {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}
      {loading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : null}

      <ul className="space-y-2">
        {rows.map((d) => (
          <li
            key={d.id}
            className="border border-border rounded-md p-3 bg-card hover:bg-muted cursor-pointer"
            onClick={() => setActiveId(d.id)}
          >
            <div className="text-sm font-medium">{d.title}</div>
            <div className="text-xs text-muted-foreground">
              {d.category} · audience: {d.audience}
              {d.lastReviewedAt ? ` · reviewed ${formatShortDate(d.lastReviewedAt)}` : ''}
            </div>
          </li>
        ))}
        {!loading && rows.length === 0 ? (
          <li className="py-6 text-center text-muted-foreground">No documents yet.</li>
        ) : null}
      </ul>

      {activeId ? (
        <ViewerDialog
          docId={activeId}
          onClose={() => setActiveId(null)}
          onChanged={() => {
            reload();
          }}
        />
      ) : null}
      {createOpen ? (
        <CreateDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ViewerDialog({
  docId,
  onClose,
  onChanged,
}: {
  docId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { tier } = useUserContext();
  const [detail, setDetail] = useState<DocumentDetail | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ document: DocumentDetail }>(`/documents/${docId}`)
      .then((r) => {
        setDetail(r.document);
        setDraft(r.document.markdown);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [docId]);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/documents/${docId}/versions`, {
        method: 'POST',
        body: JSON.stringify({ markdown: draft, changeNote: changeNote || undefined }),
      });
      setEditing(false);
      onChanged();
      const refreshed = await apiFetch<{ document: DocumentDetail }>(`/documents/${docId}`);
      setDetail(refreshed.document);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl p-6 w-full max-w-3xl space-y-3 my-8">
        {!detail ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="flex items-start justify-between border-b border-border pb-3">
              <div>
                <h3 className="text-lg font-semibold">{detail.title}</h3>
                <p className="text-xs text-muted-foreground">
                  {detail.category} · audience: {detail.audience} ·
                  {detail.version !== null ? ` v${detail.version}` : ''}
                  {detail.updatedAt ? ` · updated ${formatShortDate(detail.updatedAt)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {tier === 'manager' && !editing ? (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="text-sm underline underline-offset-4"
                  >
                    Edit
                  </button>
                ) : null}
                <button type="button" onClick={onClose} className="text-sm">
                  Close
                </button>
              </div>
            </div>

            {editing ? (
              <>
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="w-full font-mono text-xs border border-border rounded-md px-2 py-1 min-h-[300px]"
                />
                <input
                  placeholder="Change note (optional)"
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  className="w-full border border-border rounded-md px-2 py-1 text-sm"
                />
                {err ? <p className="text-sm text-destructive">{err}</p> : null}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setDraft(detail.markdown);
                    }}
                    className="px-3 py-1 text-sm"
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    onClick={() => void save()}
                    disabled={busy}
                    className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
                  >
                    {busy ? 'Saving…' : 'Save new version'}
                  </button>
                </div>
              </>
            ) : (
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(detail.markdown) }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('sop');
  const [audience, setAudience] = useState<'all' | 'managers' | 'baristas' | 'shift_leads'>('all');
  const [markdown, setMarkdown] = useState(
    '# New document\n\nReplace this body with the real content.',
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!title.trim()) {
      setErr('title required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiFetch('/documents', {
        method: 'POST',
        body: JSON.stringify({ title, category, audience, markdown }),
      });
      onCreated();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'submit failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl p-6 w-full max-w-2xl space-y-3 my-8">
        <h3 className="text-lg font-semibold">New document</h3>
        <label className="block text-sm">
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            Category
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full border border-border rounded-md px-2 py-1"
              placeholder="sop, runbook, policy…"
            />
          </label>
          <label className="block text-sm">
            Audience
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value as typeof audience)}
              className="mt-1 w-full border border-border rounded-md px-2 py-1"
            >
              <option value="all">all</option>
              <option value="managers">managers</option>
              <option value="baristas">baristas</option>
              <option value="shift_leads">shift leads</option>
            </select>
          </label>
        </div>
        <label className="block text-sm">
          Markdown
          <textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            className="mt-1 w-full font-mono text-xs border border-border rounded-md px-2 py-1 min-h-[200px]"
          />
        </label>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
